/**
 * Block-editor mission docs and the one-time clear of their legacy backups.
 */

export function useMissionDocs(localSprint, setLocalSprint, recordAction) {
  /**
   * Block-editor mission docs (Chunk 9d): `task.docUser` (user-visible) and
   * `task.docAdmin` (admin-only, gated server-side like internalNotes).
   * These are the parallel-tab replacements for notes/internalNotes — block
   * trees, not HTML. The old fields are never touched.
   */
  const handleMissionDocChange = (taskId, field, blocks, { silent = false } = {}) => {
    if (field !== 'docUser' && field !== 'docAdmin') return;
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const oldBlocks = task?.[field];

    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t =>
          t.id === taskId ? { ...t, [field]: blocks, updatedAt: new Date().toISOString() } : t
        ),
      };
    });

    // `silent` = the automatic one-time HTML import — must not enter undo history
    if (recordAction && !silent) {
      const taskName = task?.name || 'Task';
      const tierLabel = field === 'docUser' ? 'User' : 'Admin';
      recordAction({
        type: 'CHANGE_MISSION_DOC',
        description: `${taskName}: ${tierLabel} doc changed`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, [field]: oldBlocks } : t),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, [field]: blocks } : t),
            };
          });
        },
      });
    }
  };

  /**
   * Permanently clears a mission's legacy embedded content (docUser/docAdmin
   * block trees and the old notes/internalNotes HTML). Used by the manual
   * "Clear old notes" control once the user is done with the frozen backup.
   * Undoable — the removed fields are restored on undo.
   */
  const handleClearMissionLegacyNotes = (taskId) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    if (!task) return;
    const backup = {
      docUser: task.docUser,
      docAdmin: task.docAdmin,
      notes: task.notes,
      internalNotes: task.internalNotes,
    };

    const applyCleared = (t) => {
      const next = { ...t, updatedAt: new Date().toISOString() };
      delete next.docUser;
      delete next.docAdmin;
      delete next.notes;
      delete next.internalNotes;
      return next;
    };

    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t => (t.id === taskId ? applyCleared(t) : t)),
      };
    });

    if (recordAction) {
      const taskName = task?.name || 'Task';
      recordAction({
        type: 'CLEAR_MISSION_LEGACY_NOTES',
        description: `${taskName}: Cleared old notes`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => (t.id === taskId ? { ...t, ...backup } : t)),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => (t.id === taskId ? applyCleared(t) : t)),
            };
          });
        },
      });
    }
  };

  return {
    handleMissionDocChange,
    handleClearMissionLegacyNotes,
  };
}
